import * as React from 'react';
import {useContext, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import {Avatar} from '@mui/material';
import {alpha, styled} from '@mui/material/styles';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import Divider from '@mui/material/Divider';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
import {useAuth} from 'src/components/AuthProvider.tsx';
import LocalDB from 'src/utils/localDB';
import StartPagePreference from 'src/components/StartPagePreference';

const UserAvatar = styled(Avatar)(({theme}) => ({
  width: 38,
  height: 38,
  marginRight: theme.spacing(1.25),
  backgroundColor: theme.palette.primary.main,
  flexShrink: 0,
  fontSize: '0.9rem',
  fontWeight: 600,
}));

type DrawerContextValue = boolean;

/** Must be rendered under the same DrawerContext.Provider as the sidebar */
export function SidebarFooter({DrawerContext}: {DrawerContext: React.Context<DrawerContextValue>}) {
  const account = useAppSelector(selectAccount);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const {setToken} = useAuth();
  const navigate = useNavigate();
  const contextDrawerOpen = useContext(DrawerContext);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleToProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    LocalDB.remove(LocalDB.alias.userId);
    LocalDB.remove(LocalDB.alias.token);
    LocalDB.remove(LocalDB.alias.role);
    navigate('/login', {replace: true});
    handleClose();
  };

  const formatUserName = (firstName?: string, lastName?: string): string => {
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    if (lastName) return lastName;
    return '';
  };

  const getInitials = (firstName?: string, lastName?: string): string => {
    let initials = '';
    if (firstName) initials += firstName.charAt(0).toUpperCase();
    if (lastName) initials += lastName.charAt(0).toUpperCase();
    return initials || '?';
  };

  const firstName = account.userDetail?.firstName || '';
  const lastName = account.userDetail?.lastName || '';
  const email = account.userDetail?.email || '';
  const displayName = formatUserName(firstName, lastName);

  return (
    <>
      <Box
        onClick={handleProfileClick}
        sx={theme => ({
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 1.5,
          cursor: 'pointer',
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          transition: theme.transitions.create('background-color'),
          '&:hover': {
            bgcolor: alpha(theme.palette.text.primary, 0.04),
          },
        })}
      >
        <UserAvatar>{getInitials(firstName, lastName)}</UserAvatar>
        {contextDrawerOpen ? (
          <Box sx={{minWidth: 0, flex: 1}}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.8125rem',
                lineHeight: 1.3,
              }}
            >
              {displayName || email}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                lineHeight: 1.25,
                fontSize: '0.7rem',
                mt: 0.25,
              }}
            >
              {email}
            </Typography>
          </Box>
        ) : (
          <Tooltip title={displayName || email} placement="right">
            <span />
          </Tooltip>
        )}
      </Box>

      <Popover
        open={menuOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{vertical: 'center', horizontal: 'right'}}
        transformOrigin={{vertical: 'center', horizontal: 'left'}}
        PaperProps={{
          sx: {borderRadius: 2, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)'},
        }}
      >
        <List sx={{width: 228, py: 0.5}}>
          <ListItem disablePadding>
            <ListItemButton onClick={handleToProfile} sx={{py: 1}}>
              <ListItemIcon sx={{minWidth: 40}}>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Profile" primaryTypographyProps={{fontSize: '0.875rem'}} />
            </ListItemButton>
          </ListItem>
          <Divider sx={{my: 0.5}} />
          <ListItem sx={{px: 2, py: 0.75, display: 'block'}}>
            <StartPagePreference compact />
          </ListItem>
          <Divider sx={{my: 0.5}} />
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout} sx={{py: 1}}>
              <ListItemIcon sx={{minWidth: 40}}>
                <PowerSettingsNewIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" primaryTypographyProps={{fontSize: '0.875rem'}} />
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>
    </>
  );
}
